import {
  faEdit,
  faEllipsisVertical,
  faEye,
  faLayerGroup,
  faMagnifyingGlass,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomToggle from "components/Menu/CustomMenu";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ExamGroupService } from "Services/examGrouo";

interface ExamGroupItem {
  _id: string;
  name: string;
  fees?: number;
  isManualPayment?: boolean;
  paymentLink?: string;
  createdAt?: string;
}

export default function ExamGroup() {
  const navigate = useNavigate();
  const [examGroups, setExamGroups] = useState<ExamGroupItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTarget, setEditTarget] = useState<ExamGroupItem | null>(null);
  const [form, setForm] = useState({
    name: "",
    fees: "",
    isManualPayment: false,
    paymentLink: "",
  });

  const [deleteTarget, setDeleteTarget] = useState<ExamGroupItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const getAllExamGroups = async () => {
    setLoading(true);
    try {
      const res = await ExamGroupService.getAllExamGroups();
      if (res?.status === 200) {
        setExamGroups(res.data.data.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch exam groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllExamGroups();
  }, []);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return examGroups;
    return examGroups.filter((g) => g.name?.toLowerCase().includes(q));
  }, [examGroups, search]);

  const openCreateModal = () => {
    setEditTarget(null);
    setForm({ name: "", fees: "", isManualPayment: false, paymentLink: "" });
    setShowModal(true);
  };

  const openEditModal = (group: ExamGroupItem) => {
    setEditTarget(group);
    setForm({
      name: group.name || "",
      fees: String(group.fees ?? ""),
      isManualPayment: group.isManualPayment === true,
      paymentLink: group.paymentLink || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Exam group name is required");
      return;
    }
    if (form.isManualPayment && !form.paymentLink.trim()) {
      toast.error("Payment link is required for manual payment groups");
      return;
    }
    const payload: any = {
      name: form.name.trim(),
      fees: form.fees ? Number(form.fees) : 0,
      isManualPayment: form.isManualPayment,
      paymentLink: form.isManualPayment ? form.paymentLink.trim() : "",
    };
    setSaving(true);
    try {
      const res = editTarget
        ? await ExamGroupService.updateExamGroup(payload, editTarget._id)
        : await ExamGroupService.createExamGroup(payload);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(editTarget ? "Exam group updated" : "Exam group created");
        setShowModal(false);
        getAllExamGroups();
      }
    } catch (err) {
      console.error(err);
      toast.error(
        editTarget ? "Failed to update exam group" : "Failed to create exam group"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await ExamGroupService.deleteExamGroup(deleteTarget._id);
      if (res?.status === 200) {
        toast.success("Exam group deleted");
        setDeleteTarget(null);
        getAllExamGroups();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete exam group");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h5 className="mb-1 fw-bold">Exam Groups</h5>
          <span className="text-muted fs-12">
            Organise exams into groups · {examGroups.length} total
          </span>
        </div>
        <Button onClick={openCreateModal} className="d-flex align-items-center">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Exam Group
        </Button>
      </div>

      {/* Search */}
      <Row className="mb-3">
        <Col md={5} lg={4}>
          <InputGroup>
            <InputGroup.Text className="bg-white border-end-0">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="text-muted" />
            </InputGroup.Text>
            <Form.Control
              className="border-start-0 ps-0"
              placeholder="Search exam groups..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Table */}
      <Card className="border-0 shadow-sm xrg-table-card">
        <Card.Body className="p-0">
          <Table hover responsive className="xrg-table mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 64 }}>#</th>
                <th>Name</th>
                <th>Fees</th>
                <th>Created</th>
                <th className="text-center" style={{ width: 90 }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading exam groups...
                  </td>
                </tr>
              ) : filteredGroups.length > 0 ? (
                filteredGroups.map((group, index) => (
                  <tr
                    key={group._id}
                    className="xrg-table-row"
                    onClick={() => navigate("./" + group._id)}
                  >
                    <td className="text-muted">{index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className="xrg-table-icon">
                          <FontAwesomeIcon icon={faLayerGroup} />
                        </span>
                        <span className="fw-semibold">{group.name || "-"}</span>
                      </div>
                    </td>
                    <td>
                      {group.fees
                        ? <span className="fw-semibold">₹{group.fees.toLocaleString('en-IN')}</span>
                        : <span className="text-muted">Free</span>}
                    </td>
                    <td className="text-muted">
                      {group.createdAt
                        ? new Date(group.createdAt).toLocaleDateString()
                        : "-"}
                    </td>
                    <td
                      className="text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          as={CustomToggle}
                          id={`dropdown-${group._id}`}
                        >
                          <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            className="text-secondary px-2"
                            style={{ cursor: "pointer" }}
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
                          <Dropdown.Item onClick={() => navigate("./" + group._id)}>
                            <FontAwesomeIcon icon={faEye} className="text-info" />
                            <span className="fw-bold text-secondary fs-12 ms-2">
                              View
                            </span>
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => openEditModal(group)}>
                            <FontAwesomeIcon icon={faEdit} className="text-info" />
                            <span className="fw-bold text-secondary fs-12 ms-2">
                              Edit
                            </span>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => setDeleteTarget(group)}>
                            <FontAwesomeIcon icon={faTrash} className="text-danger" />
                            <span className="fw-bold text-danger fs-12 ms-2">
                              Delete
                            </span>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-5">
                    <span className="xrg-table-icon mx-auto mb-3 d-flex">
                      <FontAwesomeIcon icon={faLayerGroup} />
                    </span>
                    <h6 className="fw-bold mb-1">No exam groups found</h6>
                    <p className="text-muted fs-12 mb-0">
                      {search
                        ? "Try a different search term."
                        : "Create your first exam group to get started."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">
            {editTarget ? "Edit Exam Group" : "Add Exam Group"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 fw-semibold">Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter exam group name"
                value={form.name}
                autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fs-12 fw-semibold">Fees (₹)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                placeholder="Enter fees (0 for free)"
                value={form.fees}
                onChange={(e) => setForm({ ...form, fees: e.target.value })}
              />
              <Form.Text className="text-muted">
                Set to 0 for free groups. Students must pay this amount to access exams in the group.
              </Form.Text>
            </Form.Group>
            <Form.Group className={form.isManualPayment ? "mb-3" : ""}>
              <Form.Check
                type="switch"
                id="exam-group-manual-payment"
                label="Manual payment"
                checked={form.isManualPayment}
                onChange={(e) =>
                  setForm({ ...form, isManualPayment: e.target.checked })
                }
              />
              <Form.Text className="text-muted">
                Collect this group's fees outside Razorpay. Students pay through the link
                below and an admin approves each payment manually.
              </Form.Text>
            </Form.Group>
            {form.isManualPayment && (
              <Form.Group>
                <Form.Label className="fs-12 fw-semibold">Payment Link</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://..."
                  value={form.paymentLink}
                  onChange={(e) =>
                    setForm({ ...form, paymentLink: e.target.value })
                  }
                />
                <Form.Text className="text-muted">
                  Students are redirected here to complete the payment.
                </Form.Text>
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 fw-bold">Delete Exam Group</Modal.Title>
        </Modal.Header>
        <Modal.Body className="fs-12">
          Are you sure you want to delete{" "}
          <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
